import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useActions, useCards } from "../../utils/reactQueryHooks";
import {
    isActuatorProbePaused,
    pingActuatorPresence,
    publishMQTTStatus,
    subscribeActuatorActionReports,
    subscribeActuatorHeartbeats,
    waitForReportedPair,
} from "../../utils/mqttUtils";
import { isIrrigationActuator } from "../../utils/actionUtils";
import { useNow } from "../../hooks/useNow";

const PROBE_INTERVAL_MS = 60 * 1000;
const STAGGER_MS = 1500;
/** Irrigation firmware publishes a heartbeat about once a minute. */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
/** Stay online through two missed beats or probe cycles, then show offline. */
const ONLINE_WINDOW_MS = HEARTBEAT_INTERVAL_MS * 3;

const ActuatorPresenceContext = createContext(null);

const normalizeMac = (mac) =>
    typeof mac === "string" ? mac.trim().replace(/;/g, ":").toUpperCase() : "";

const usesHeartbeat = (actuatorType) => isIrrigationActuator(actuatorType);

const shouldProbe = (action) =>
    Boolean(
        action?.macAddress
        && action?.sensor
        && action.actuatorType !== "camera"
        && !usesHeartbeat(action.actuatorType)
    );

/**
 * Tracks actuator presence while the app is open.
 * Irrigation: passive MQTT heartbeats from firmware.
 * Light and other MQTT actuators: staggered status pings (no settings change).
 */
export function ActuatorPresenceProvider({ children }) {
    const { data: actions = [] } = useActions();
    const { data: cards = [] } = useCards();
    const [heartbeatSeen, setHeartbeatSeen] = useState({});
    const [pingPresence, setPingPresence] = useState({});
    const [listeningSince, setListeningSince] = useState(null);
    const [reportedPairs, setReportedPairs] = useState({});
    const [repairStatus, setRepairStatus] = useState({});
    const actionsRef = useRef(actions);
    const cardsRef = useRef(cards);
    actionsRef.current = actions;
    cardsRef.current = cards;

    const probeKey = actions
        .filter(shouldProbe)
        .map((action) => `${normalizeMac(action.macAddress)}:${normalizeMac(action.sensor)}`)
        .sort()
        .join("|");

    useEffect(() => {
        setListeningSince(Date.now());
        const unsubscribeHeartbeats = subscribeActuatorHeartbeats((mac, seenAt) => {
            setHeartbeatSeen((prev) => (
                prev[mac] === seenAt ? prev : { ...prev, [mac]: seenAt }
            ));
        });
        const unsubscribeReports = subscribeActuatorActionReports((mac, paired) => {
            setReportedPairs((prev) => {
                const current = prev[mac];
                if (current?.paired === paired) {
                    if (current.streak >= 2) return prev;
                    return { ...prev, [mac]: { paired, streak: current.streak + 1 } };
                }
                return { ...prev, [mac]: { paired, streak: 1 } };
            });
        });
        return () => {
            unsubscribeHeartbeats();
            unsubscribeReports();
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;
        let pending = [];

        const clearPending = () => {
            pending.forEach(clearTimeout);
            pending = [];
        };

        const probeAll = () => {
            if (document.visibilityState !== "visible") return;
            clearPending();
            const list = actionsRef.current.filter(shouldProbe);
            list.forEach((action, index) => {
                const timer = setTimeout(() => {
                    if (cancelled || document.visibilityState !== "visible") return;
                    const mac = normalizeMac(action.macAddress);
                    pingActuatorPresence(mac, action.sensor).then((result) => {
                        if (cancelled || result === "skipped") return;
                        setPingPresence((prev) => ({
                            ...prev,
                            [mac]: result === "success"
                                ? { status: "online", lastSeenAt: Date.now() }
                                : { status: "offline", lastSeenAt: prev[mac]?.lastSeenAt ?? null },
                        }));
                    });
                }, index * STAGGER_MS);
                pending.push(timer);
            });
        };

        const stop = () => {
            clearPending();
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const start = () => {
            if (intervalId || document.visibilityState !== "visible") return;
            probeAll();
            intervalId = setInterval(probeAll, PROBE_INTERVAL_MS);
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                start();
            } else {
                stop();
            }
        };

        start();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            cancelled = true;
            stop();
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [probeKey]);

    const getPresence = (macAddress, now, actuatorType) => {
        if (!actuatorType || actuatorType === "camera") {
            return null;
        }

        const mac = normalizeMac(macAddress);
        if (!mac) {
            return null;
        }

        if (usesHeartbeat(actuatorType)) {
            const seenAt = heartbeatSeen[mac];
            if (seenAt != null && now - seenAt <= ONLINE_WINDOW_MS) {
                return { status: "online", lastSeenAt: seenAt, source: "heartbeat" };
            }
            if (seenAt != null) {
                return { status: "offline", lastSeenAt: seenAt, source: "heartbeat" };
            }
            if (listeningSince == null || now - listeningSince < ONLINE_WINDOW_MS) {
                return { status: "unknown", lastSeenAt: null, source: "heartbeat" };
            }
            return { status: "offline", lastSeenAt: null, source: "heartbeat" };
        }

        const row = pingPresence[mac];
        if (isActuatorProbePaused(mac) && row?.status === "online") {
            return { status: "online", lastSeenAt: row.lastSeenAt, source: "ping" };
        }
        if (row?.status === "online" && row.lastSeenAt != null && now - row.lastSeenAt <= ONLINE_WINDOW_MS) {
            return { status: "online", lastSeenAt: row.lastSeenAt, source: "ping" };
        }
        if (row?.lastSeenAt != null) {
            return { status: "offline", lastSeenAt: row.lastSeenAt, source: "ping" };
        }
        if (listeningSince == null || now - listeningSince < ONLINE_WINDOW_MS) {
            return { status: "unknown", lastSeenAt: null, source: "ping" };
        }
        return { status: "offline", lastSeenAt: null, source: "ping" };
    };

    const mismatches = useMemo(() => {
        return actions
            .filter((action) => action.actuatorType && action.actuatorType !== "camera")
            .map((action) => {
                const mac = normalizeMac(action.macAddress);
                const dashboardSensor = normalizeMac(action.sensor);
                const report = reportedPairs[mac];
                if (!mac || !dashboardSensor || !report || report.streak < 2) return null;
                if (report.paired === dashboardSensor) return null;
                const card = cards.find((candidate) => normalizeMac(candidate.macAddress) === dashboardSensor);
                return {
                    macAddress: mac,
                    actuatorDescription: action.actuatorDescription || mac,
                    dashboardSensor,
                    dashboardLabel: card?.sensorDescription || action.sensor,
                    reportedSensor: report.paired,
                };
            })
            .filter(Boolean);
    }, [actions, cards, reportedPairs]);

    const repairPairing = async (macAddress) => {
        const mac = normalizeMac(macAddress);
        const action = actionsRef.current.find((candidate) => normalizeMac(candidate.macAddress) === mac);
        const dashboardSensor = normalizeMac(action?.sensor);
        const card = cardsRef.current.find((candidate) => normalizeMac(candidate.macAddress) === dashboardSensor);
        if (!action || !dashboardSensor) {
            setRepairStatus((prev) => ({
                ...prev,
                [mac]: { state: "error", message: "This actuator is not linked to a dashboard zone." },
            }));
            return false;
        }

        setRepairStatus((prev) => ({ ...prev, [mac]: { state: "updating", message: "" } }));

        const irrigation = isIrrigationActuator(action.actuatorType);
        const result = irrigation
            ? await publishMQTTStatus(
                action.macAddress,
                action.sensor,
                {
                    water: { low: card?.waterLow, high: card?.waterHigh },
                    water2: { low: card?.water2Low, high: card?.water2High },
                },
                "X",
                action.duration ?? 4,
                action.sensorTracking ? 1 : 0
            )
            : await publishMQTTStatus(
                action.macAddress,
                action.sensor,
                undefined,
                "X",
                "X",
                "X",
                true
            );

        const accepted = result === "success" || result === "accepted-ping";
        if (!accepted) {
            setRepairStatus((prev) => ({
                ...prev,
                [mac]: { state: "error", message: "The device did not accept the update." },
            }));
            return false;
        }

        const confirmed = await waitForReportedPair(mac, dashboardSensor);
        if (!confirmed && result === "success") {
            setRepairStatus((prev) => ({
                ...prev,
                [mac]: { state: "error", message: "The device is still reporting the old sensor." },
            }));
            return false;
        }

        if (!confirmed) {
            setReportedPairs((prev) => ({
                ...prev,
                [mac]: { paired: dashboardSensor, streak: 2 },
            }));
        }

        setRepairStatus((prev) => ({
            ...prev,
            [mac]: {
                state: "success",
                message: confirmed
                    ? "Device pairing matches the dashboard."
                    : "Device accepted the dashboard sensor. It confirms on its next status report.",
            },
        }));
        setTimeout(() => {
            setRepairStatus((prev) => {
                if (prev[mac]?.state !== "success") return prev;
                const next = { ...prev };
                delete next[mac];
                return next;
            });
        }, 6000);
        return true;
    };

    const contextValue = {
        getPresence,
        mismatches,
        repairStatus,
        repairPairing,
    };

    return (
        <ActuatorPresenceContext.Provider value={contextValue}>
            {children}
        </ActuatorPresenceContext.Provider>
    );
}

ActuatorPresenceProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

/**
 * Presence for one actuator. Outside the provider this returns null.
 */
export function useActuatorPresence(macAddress, actuatorType) {
    const context = useContext(ActuatorPresenceContext);
    const now = useNow(30000);
    if (!context?.getPresence) return null;
    return context.getPresence(macAddress, now, actuatorType);
}

export function usePairingMismatches() {
    const context = useContext(ActuatorPresenceContext);
    return context?.mismatches ?? [];
}

export function usePairingRepair() {
    const context = useContext(ActuatorPresenceContext);
    return {
        repairPairing: context?.repairPairing ?? (async () => false),
        repairStatus: context?.repairStatus ?? {},
    };
}
